/**
 * Scroll Depth Tracker
 * Tracks how far users scroll down the page and sends events at specific thresholds
 */

class ScrollDepthTracker {
  constructor(options = {}) {
    // Default options
    this.options = Object.assign({
      // Percentage thresholds to trigger events at
      thresholds: [50, 90],
      // Throttle frequency in milliseconds
      throttleDelay: 250,
      // Event name prefix
      eventPrefix: 'scroll_depth_',
      // Whether to track unique thresholds only once per page
      trackThresholdsOnlyOnce: true
    }, options);

    // Keep track of thresholds that have been triggered
    this.triggeredThresholds = new Set();
    
    // Throttle the scroll event handler
    this.handleScroll = this._throttle(this._checkScrollDepth.bind(this), this.options.throttleDelay);
    
    // Initialize the tracker
    this.init();
  }
  
  // Initialize the scroll depth tracker
  init() {
    // Add event listener for scroll events
    window.addEventListener('scroll', this.handleScroll);
    
    // Also check on page load (in case the page is pre-scrolled)
    window.addEventListener('load', this.handleScroll);
    
    // Check on resize as well, since page dimensions might change
    window.addEventListener('resize', this.handleScroll);
    
  }
  
  // Clean up event listeners
  destroy() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleScroll);
  }
  
  // Check current scroll depth and trigger events if thresholds are crossed
  _checkScrollDepth() {
    // Calculate scroll depth as a percentage
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = Math.max(
      document.body.scrollHeight, 
      document.documentElement.scrollHeight,
      document.body.offsetHeight, 
      document.documentElement.offsetHeight,
      document.body.clientHeight, 
      document.documentElement.clientHeight
    );
    
    // If documentHeight is the same as windowHeight, we're on a page with no scroll
    if (documentHeight <= windowHeight) {
      return;
    }
    
    // Calculate how far the user has scrolled as a percentage
    const scrollableDistance = documentHeight - windowHeight;
    const scrollPercentage = Math.round((scrollTop / scrollableDistance) * 100);
    
    // Check each threshold
    this.options.thresholds.forEach(threshold => {
      // If we've passed this threshold and haven't triggered it yet (or if we want to track multiple times)
      if (
        scrollPercentage >= threshold && 
        (!this.options.trackThresholdsOnlyOnce || !this.triggeredThresholds.has(threshold))
      ) {
        this._triggerEvent(threshold, scrollPercentage);
        this.triggeredThresholds.add(threshold);
      }
    });
  }
  
  // Trigger the analytics event for a threshold
  _triggerEvent(threshold, exactPercentage) {
    const eventName = 'scroll_depth';
    const eventMetadata = {
      threshold: threshold,
      exact_percentage: exactPercentage,
      page_url: window.location.href,
      page_title: document.title,
      timestamp: new Date().toISOString()
    };
    
    // Use the global trackTelemetryEvent function if available
    if (typeof window.trackTelemetryEvent === 'function') {
      window.trackTelemetryEvent(eventName, threshold, eventMetadata);
    } 
    // Fallback to direct fbq call if trackTelemetryEvent isn't available
    else if (typeof fbq !== 'undefined') {
      fbq('trackCustom', eventName, eventMetadata);
    }
  }
  
  // Throttle function to limit how often the scroll depth is checked
  _throttle(func, delay) {
    let lastCall = 0;
    return function(...args) {
      const now = new Date().getTime();
      if (now - lastCall >= delay) {
        lastCall = now;
        func(...args);
      }
    };
  }
}

// Initialize the scroll depth tracker when the DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.scrollDepthTracker = new ScrollDepthTracker();
}); 